import React, { useState } from 'react';
import { observer } from 'mobx-react';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { DfmStatus } from 'libs/rendering/meshAnalysis/appManager';
import { getUnitsLabel } from 'apps/rfq/dfmConfig';

import color from 'styles/themes/wikifactory/styles/colors';
import { Heading, Box, SvgIcon, Radio } from 'components';
import { InfoIcon, Warning } from 'icons';

import { DFMIssues } from 'apps/rfq/rfqPage/views/DFMIssues';

import { DfmViewportWrapper } from './dfmAnalysis.styled';

const DfmAnalysis = observer(function _DfmAnalysis({ body }) {
  const viewerControl = useViewerControl();

  // get units from cadFile
  const fileMetadata = viewerControl.cadFile.metadata;
  const modeller = fileMetadata?.modeller || 'CAD';
  const cadUnits = getUnitsLabel(fileMetadata?.modelUnits);
  const canChangeUnits = body.isFaceted || !cadUnits;

  const [units, SetUnits] = useState(canChangeUnits ? 'mm' : cadUnits);

  // get the currently running DFM analysis from viewerControl
  const { dfmError, dfmStatus, isWaiting } = viewerControl.meshAnalysis;

  /*
    render
  */

  const statusMsg = () => {
    if (dfmError) {
      return 'Sorry, an error occurred during the DFM analysis of this file';
    }

    if (isWaiting) {
      if (
        dfmStatus === DfmStatus.SUBSCRIBED ||
        dfmStatus === DfmStatus.REQUESTED
      ) {
        return 'Preparing the DFM analysis request for your part...';
      }
      if (
        dfmStatus === DfmStatus.FINISHED ||
        dfmStatus === DfmStatus.FETCHING
      ) {
        return 'Loading Dfm Analysis results...';
      }
      return 'Running the DFM analysis for your part...';
    }

    return null;
  };

  return (
    <DfmViewportWrapper>
      <Heading level='1' size='large' margin='0.5rem 0'>
        DFM Analysis
      </Heading>

      {/* Units */}
      <Box
        mb={1}
        sx={{
          display: 'grid',
          gridTemplateColumns: '288px auto',
          gridGap: '20px',
        }}
      >
        <Box>
          <Heading level={6} size='default' weight='xbold'>
            Units
          </Heading>
          {/* radio buttons */}
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'auto auto auto',
            }}
          >
            <Radio
              id='units_mm'
              value='mm'
              groupName='units'
              size='small'
              label='mm'
              checked={units === 'mm'}
              disabled={!canChangeUnits && units !== 'mm'}
              onChange={() => SetUnits('mm')}
            />
            <Radio
              id='units_cm'
              value='cm'
              groupName='units'
              size='small'
              label='cm'
              checked={units === 'cm'}
              disabled={!canChangeUnits && units !== 'cm'}
              onChange={() => SetUnits('cm')}
            />
            <Radio
              id='units_inch'
              value='inch'
              groupName='units'
              size='small'
              label='inch'
              checked={units === 'inch'}
              disabled={!canChangeUnits && units !== 'inch'}
              onChange={() => SetUnits('inch')}
            />
          </Box>
        </Box>

        {/* units msg */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
          }}
        >
          <SvgIcon
            icon={!canChangeUnits ? <InfoIcon /> : <Warning />}
            color={!canChangeUnits ? 'minimal' : ''}
          />
          <Box
            ml={1}
            sx={{
              color: color.wfGrey500,
              margin: '0 0.5rem',
            }}
          >
            {canChangeUnits
              ? `${modeller} file has no units.`
              : `${modeller} file units.`}
          </Box>
        </Box>
      </Box>

      {/* Status message */}
      {statusMsg() && (
        <Box mb={3} sx={{ display: 'flex' }}>
          <SvgIcon icon={<InfoIcon />} size='medium' />
          <Box ml={2}>{statusMsg()}</Box>
        </Box>
      )}

      {/* Waring List */}
      {!dfmError && !isWaiting && <DFMIssues body={body} toolUnits={units} />}
    </DfmViewportWrapper>
  );
});

export default DfmAnalysis;
